<template>
  <div class="SubjectSelection">
    <el-cascader
      :style="{ lineHeight: '30px', width: width }"
      :options="options"
      v-model="subValue"
      :props="cascaderProps"
      clearable
      :emit-path="single"
      filterable
      :placeholder="single ? '请选择' : '请选择学科'"
    ></el-cascader>
  </div>
</template>

<script>
import { Get } from '@/api'
import ProjectConfig from '@/api'
const increment = ProjectConfig.increment

export default {
  name: 'SubjectSelection',
  props: {
    value: {
      type: [Array, String, Number],
      default: () => []
    },
    single: {
      type: Boolean,
      default: false
    },
    width: {
      type: String,
      default: '710px'
    },
    // 级联节点的值字段，默认 'code'，可传 'name' 使 v-model 直接使用 name
    valueField: {
      type: String,
      default: 'code'
    },
    // 级联节点的标签字段，默认 'name'
    labelField: {
      type: String,
      default: 'name'
    }
  },
  data() {
    return {
      subValue: this.single ? null : [],
      options: []
    }
  },
  computed: {
    cascaderProps() {
      return {
        multiple: !this.single,
        checkStrictly: true,
        value: this.valueField,
        label: this.labelField,
        children: 'children'
      }
    }
  },
  watch: {
    value: {
      handler(newValue) {
        if (this.single) {
          if (!newValue) {
            this.subValue = null
            return
          }
          // 单选模式：value 是叶子 code，需要在 options 加载完成后反查完整路径
          if (this.options.length > 0) {
            this.subValue = this.findPathByValue(newValue) || null
          }
          return
        }
        if (!newValue) {
          this.subValue = []
          return
        }
        // 多选模式：value 是叶子值数组，需要反查完整路径
        if (this.options.length > 0) {
          const paths = newValue
            .map((v) => this.findPathByValue(v))
            .filter(Boolean)
          if (JSON.stringify(paths) !== JSON.stringify(this.subValue)) {
            this.subValue = paths
          }
        }
      },
      deep: true
    },
    subValue: {
      handler(newValue) {
        if (this.single) {
          if (!newValue || !Array.isArray(newValue) || newValue.length === 0) {
            if (this.value) {
              this.$emit('input', '')
              this.$emit('change', '')
            }
            return
          }
          // 单选模式：取路径最后一个 code 作为值
          const leafCode = newValue[newValue.length - 1]
          if (String(leafCode) !== String(this.value)) {
            this.$emit('input', leafCode)
            this.$emit('change', leafCode)
          }
          return
        }
        // 多选模式：只传叶子节点值
        const emittedValue = newValue.map((item) => item[item.length - 1])
        if (JSON.stringify(emittedValue) !== JSON.stringify(this.value)) {
          this.$emit('input', emittedValue)
          this.$emit('change', emittedValue)
        }
      },
      deep: true
    }
  },
  mounted() {
    this.fetchSubjectData().then((nodes) => {
      this.options = nodes
      // 根节点加载完成后，抛出第一个根节点供父组件获取其 name
      if (nodes[0]) {
        this.$emit('root-loaded', nodes[0])
      }
      // 数据加载完成后，根据 value 回显 subValue
      if (this.single) {
        if (this.value) {
          this.subValue = this.findPathByValue(this.value) || null
        }
        return
      }
      // 多选模式回显：value 是叶子值数组，反查完整路径
      if (this.value && this.value.length) {
        const paths = this.value
          .map((v) => this.findPathByValue(v))
          .filter(Boolean)
        this.subValue = paths
      } else {
        this.subValue = []
      }
    })
  },
  methods: {
    async fetchSubjectData() {
      try {
        const params = { depth: 3, code: '' }
        const res = await Get(increment.GetSubject.url, { params })
        if (res.data.status === 0) {
          // 接口返回 {code:"father", name:"学科代码", children:[一级...]}
          // 这个根 wrapper 必须保留为顶级 options，让 cascader 第一级显示它
          // 同时父组件 PushSettings 依赖路径第一项 = 根节点 name 来做 slice(1)/回显拼装
          const rawRoot = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data
          if (!rawRoot) return []
          const formattedRoot = this.formatNodeTree(rawRoot)
          return [formattedRoot]
        }
        return []
      } catch (error) {
        console.log('Error fetching subject data:', error)
        return []
      }
    },
    // 递归格式化节点：保留 code/name，递归处理 children，叶子节点设 children 为 undefined
    formatNodeTree(item) {
      const hasChildren = item.children && item.children.length > 0
      return {
        code: item.code,
        name: item.name,
        children: hasChildren
          ? item.children.map((c) => this.formatNodeTree(c))
          : undefined
      }
    },
    // 在 options 树中查找某个值的完整路径（单选回显用）
    findPathByValue(targetValue) {
      const search = (nodes, path) => {
        for (const node of nodes) {
          const currentPath = [...path, node[this.valueField]]
          if (String(node[this.valueField]) === String(targetValue)) {
            return currentPath
          }
          if (node.children && node.children.length > 0) {
            const result = search(node.children, currentPath)
            if (result) return result
          }
        }
        return null
      }
      return search(this.options, [])
    },
    clear() {
      this.subValue = this.single ? null : []
    }
  }
}
</script>

<style lang="scss" scoped>
.SubjectSelection {
  width: 100%;
  ::v-deep .el-cascader__search-input {
    margin-left: 10px !important;
  }
  ::v-deep .el-cascader .el-input .el-input__inner {
    height: 30px;
  }
}
</style>
